import { useState } from 'react';
import { ExternalLink, Linkedin, ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';
import { PixelPrincePortrait } from '../PixelCharacters';

export function HeroSection({ flipCardHint = "flip to reveal your new fav character" }) {
    const [isFlipped, setIsFlipped] = useState(false);

    return (
        <section 
            className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
            data-testid="hero-section"
        >
            {/* Background with overlay */}
            <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{
                    backgroundImage: `url('https://images.pexels.com/photos/36162476/pexels-photo-36162476.jpeg')`,
                }}
            />
            <div className="absolute inset-0 bg-background/90 dark:bg-background/95" />
            
            {/* Scanlines overlay */}
            <div className="absolute inset-0 scanlines pointer-events-none" />

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                    {/* Text Content - Left side */}
                    <div className="md:col-span-8 space-y-8">
                        <div className="space-y-4">
                            <p 
                                className="text-xs uppercase tracking-[0.3em] text-primary font-bold animate-fade-in-up"
                                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                            >
                                Welcome, Adventurer
                            </p>
                            
                            <h1 
                                className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase blinking-cursor animate-fade-in-up animation-delay-100"
                                style={{ fontFamily: "'Silkscreen', cursive" }}
                                data-testid="hero-title"
                            >
                                Shreyan Sahukar
                            </h1>
                            
                            <p 
                                className="text-lg sm:text-xl text-muted-foreground max-w-2xl animate-fade-in-up animation-delay-200"
                                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                            >
                                Product leader in fintech. Building at the intersection of payments, cross-border finance, and emerging markets.
                            </p>
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
                            <a 
                                href="https://www.linkedin.com/in/shreyansahukar/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-linkedin-cta"
                            >
                                <Button 
                                    className="rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn text-sm uppercase tracking-widest px-6 py-5"
                                >
                                    <Linkedin className="h-4 w-4 mr-2" />
                                    Connect on LinkedIn
                                    <ExternalLink className="h-3 w-3 ml-2" />
                                </Button>
                            </a>
                            
                            <a href="#writings">
                                <Button 
                                    variant="outline"
                                    className="rounded-none border-2 retro-btn text-sm uppercase tracking-widest px-6 py-5"
                                    data-testid="hero-writings-cta"
                                >
                                    Read My Writings
                                </Button>
                            </a>
                        </div>

                        {/* Stats/Quick Info */}
                        <div className="flex flex-wrap gap-8 pt-8 border-t-2 border-dashed border-border animate-fade-in-up animation-delay-400">
                            <div>
                                <p className="text-xs uppercase tracking-widest text-muted-foreground">Focus</p>
                                <p className="text-sm font-bold text-primary">Fintech & Payments</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-widest text-muted-foreground">Experience</p>
                                <p className="text-sm font-bold">8+ Years</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-widest text-muted-foreground">Domains</p>
                                <p className="text-sm font-bold">Product, Strategy, Data</p>
                            </div>
                        </div>
                    </div>

                    {/* Visual Element - Right side - FLIP CARD */}
                    <div className="md:col-span-4 flex justify-center mt-8 md:mt-0">
                        <div 
                            className="relative cursor-pointer flip-card-container"
                            onClick={() => setIsFlipped(!isFlipped)}
                            data-testid="hero-flip-card"
                            style={{ perspective: '1000px' }}
                        >
                            <div 
                                className={`flip-card-inner transition-transform duration-700 ${isFlipped ? 'flipped' : ''}`}
                                style={{ 
                                    transformStyle: 'preserve-3d',
                                    width: '220px',
                                    height: '260px'
                                }}
                            >
                                {/* FRONT - Prince Portrait */}
                                <div 
                                    className="flip-card-front absolute inset-0 w-full h-full border-4 border-primary bg-card"
                                    style={{ backfaceVisibility: 'hidden' }}
                                >
                                    <PixelPrincePortrait flipCardHint={flipCardHint} />
                                    {/* Pixel corners */}
                                    <div className="absolute -top-2 -left-2 w-4 h-4 bg-primary" />
                                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-primary" />
                                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-primary" />
                                    <div className="absolute -bottom-2 -right-2 w-4 h-4 bg-primary" />
                                </div>

                                {/* BACK - Actual Photo */}
                                <div 
                                    className="flip-card-back absolute inset-0 w-full h-full border-4 border-primary bg-card overflow-hidden"
                                    style={{ 
                                        backfaceVisibility: 'hidden',
                                        transform: 'rotateY(180deg)'
                                    }}
                                >
                                    {/* Photo */}
                                    <div 
                                        className="w-full h-full"
                                        style={{
                                            backgroundImage: `url('https://customer-assets.emergentagent.com/job_shreyan-forge/artifacts/bmi06ouc_1551525514797.jpeg')`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center top',
                                            imageRendering: 'pixelated',
                                            filter: 'contrast(1.15) saturate(0.9)',
                                        }}
                                    />
                                    {/* Scanline overlay for CRT/retro effect */}
                                    <div 
                                        className="absolute inset-0 pointer-events-none"
                                        style={{
                                            backgroundImage: `
                                                repeating-linear-gradient(
                                                    0deg,
                                                    transparent,
                                                    transparent 2px,
                                                    rgba(0,0,0,0.15) 2px,
                                                    rgba(0,0,0,0.15) 4px
                                                )
                                            `,
                                        }}
                                    />
                                    {/* Pixel grid overlay */}
                                    <div 
                                        className="absolute inset-0 pointer-events-none opacity-30"
                                        style={{
                                            backgroundImage: `
                                                linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px),
                                                linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)
                                            `,
                                            backgroundSize: '8px 8px'
                                        }}
                                    />
                                    {/* Defeat message */}
                                    <div className="absolute bottom-0 left-0 right-0 bg-primary/90 py-2 text-center">
                                        <span 
                                            className="text-primary-foreground text-[10px] uppercase tracking-wider"
                                            style={{ fontFamily: "'Silkscreen', cursive" }}
                                        >
                                            Oops. Bad luck. Try again next time.
                                        </span>
                                    </div>
                                    {/* Pixel corners */}
                                    <div className="absolute -top-2 -left-2 w-4 h-4 bg-primary" />
                                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-primary" />
                                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-primary" />
                                    <div className="absolute -bottom-2 -right-2 w-4 h-4 bg-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                <a href="#career" className="text-muted-foreground hover:text-primary transition-colors">
                    <ArrowDown className="h-6 w-6" />
                </a>
            </div>
        </section>
    );
}
